import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  copySurvey,
  deleteSurvey,
  updateSurveyStatus,
  type TSurveyListItem,
  type TSurveyResponseFormat,
  type TSurveyStatus,
  type TSurveyStatusUpdate,
} from 'api/surveys'
import { Alert } from 'components/Alert'
import { Button } from 'components/Button'
import { ConfirmModal } from 'components/ConfirmModal'
import { pageCardClasses } from 'components/AppLayout/styles'
import { routes } from 'constants/routes'
import { useOrgPath } from 'hooks/useOrgPath'
import { useSurveys } from 'hooks/useSurveys'
import { cn } from 'utils/cn'
import { getSurveyErrorMessage } from './errors'

const STATUS_LABELS: Record<TSurveyStatus, string> = {
  DRAFT: '下書き',
  ACTIVE: '公開中',
  CLOSED: '終了',
}

// TagChip と同系の淡い背景 + 濃い文字でステータスを色分けする。
const STATUS_BADGE_CLASSES: Record<TSurveyStatus, string> = {
  DRAFT: 'bg-[#F1F5F9] text-[#334155]',
  ACTIVE: 'bg-[#DCFCE7] text-[#15803D]',
  CLOSED: 'bg-[#FFEDD5] text-[#C2410C]',
}

const RESPONSE_FORMAT_LABELS: Record<TSurveyResponseFormat, string> = {
  FORM: 'フォーム回答',
  TALK: 'トーク回答',
}

type TStatusAction = {
  label: string
  nextStatus: TSurveyStatusUpdate
  title: string
  confirm: string
  message: (name: string) => string
}

const STATUS_ACTIONS: Record<TSurveyStatus, TStatusAction> = {
  DRAFT: {
    label: '公開する',
    nextStatus: 'ACTIVE',
    title: 'アンケートを公開しますか？',
    confirm: '公開する',
    message: (name) =>
      `「${name}」を公開します。友だちが回答できるようになります。`,
  },
  ACTIVE: {
    label: '終了する',
    nextStatus: 'CLOSED',
    title: 'アンケートを終了しますか？',
    confirm: '終了する',
    message: (name) =>
      `「${name}」を終了します。新規の回答を受け付けなくなります。`,
  },
  CLOSED: {
    label: '再公開する',
    nextStatus: 'ACTIVE',
    title: 'アンケートを再公開しますか？',
    confirm: '再公開する',
    message: (name) =>
      `「${name}」を再公開します。友だちが再び回答できるようになります。`,
  },
}

type TPendingAction =
  | { type: 'delete'; survey: TSurveyListItem }
  | { type: 'status'; survey: TSurveyListItem; action: TStatusAction }

const formatSurveyCreatedAt = (iso: string) =>
  new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    timeZone: 'Asia/Tokyo',
  }).format(new Date(iso))

export default function SurveyManagement() {
  const navigate = useNavigate()
  const orgPathFor = useOrgPath()
  const { surveys, loading, error: listError, refetch } = useSurveys()
  const [pendingAction, setPendingAction] = useState<TPendingAction | null>(
    null,
  )
  const [busyId, setBusyId] = useState<number | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const actionInFlight = useRef(false)

  const closePendingAction = () => setPendingAction(null)

  const handleCopy = async (survey: TSurveyListItem) => {
    if (actionInFlight.current) return
    actionInFlight.current = true
    setBusyId(survey.id)
    setActionError(null)
    setSuccessMessage(null)
    try {
      const copied = await copySurvey(survey.id)
      setSuccessMessage(`「${copied.name}」を作成しました。`)
      await refetch()
    } catch (error: unknown) {
      setActionError(
        getSurveyErrorMessage(error, 'アンケートのコピーに失敗しました。'),
      )
    } finally {
      actionInFlight.current = false
      setBusyId(null)
    }
  }

  const handleConfirmPendingAction = async () => {
    if (!pendingAction || actionInFlight.current) return
    actionInFlight.current = true
    setBusyId(pendingAction.survey.id)
    setActionError(null)
    try {
      if (pendingAction.type === 'delete') {
        await deleteSurvey(pendingAction.survey.id)
      } else {
        await updateSurveyStatus(
          pendingAction.survey.id,
          pendingAction.action.nextStatus,
        )
      }
      setPendingAction(null)
      setSuccessMessage(null)
      await refetch()
    } catch (error: unknown) {
      setActionError(
        getSurveyErrorMessage(
          error,
          pendingAction.type === 'delete'
            ? 'アンケートの削除に失敗しました。'
            : 'アンケートの公開状態の変更に失敗しました。',
        ),
      )
    } finally {
      actionInFlight.current = false
      setBusyId(null)
    }
  }

  const pendingDelete = pendingAction?.type === 'delete' ? pendingAction : null
  const pendingStatus = pendingAction?.type === 'status' ? pendingAction : null

  return (
    <>
      <section className={`w-full ${pageCardClasses.large}`}>
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div className="grid gap-2">
            <span className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-muted">
              survey management
            </span>
            <h1 className="text-2xl font-display text-ink">アンケート管理</h1>
            <p className="text-sm text-muted">
              フォーム回答型のアンケートを作成し、回答に応じて友だちへタグを付けられます。
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={() => navigate(orgPathFor(routes.SURVEY_MANAGEMENT_CREATE))}
          >
            新規作成
          </Button>
        </header>

        {successMessage ? (
          <p
            className="mb-4 rounded-2xl border border-[#BBF7D0] bg-[#DCFCE7] px-4 py-3 text-sm text-[#15803D]"
            role="status"
          >
            {successMessage}
          </p>
        ) : null}

        {listError || actionError ? (
          <div className="mb-4 space-y-3">
            <Alert>{actionError ?? listError}</Alert>
            {listError ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={loading}
                onClick={() => void refetch()}
              >
                再読み込み
              </Button>
            ) : null}
          </div>
        ) : null}

        {loading ? (
          <p role="status" className="text-sm text-muted">
            読み込み中...
          </p>
        ) : null}
        {!loading && surveys.length === 0 && !listError ? (
          <div className="grid justify-items-start gap-3 rounded-2xl border border-dashed border-border bg-surface/60 p-8">
            <p className="text-sm text-muted">
              アンケートはまだありません。「新規作成」から最初のアンケートを作成しましょう。
            </p>
          </div>
        ) : null}

        <div className="grid gap-3">
          {surveys.map((survey) => {
            const statusAction = STATUS_ACTIONS[survey.status]
            return (
              <div
                key={survey.id}
                className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-border/70 bg-white p-5 shadow-sm"
              >
                <div className="grid gap-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={cn(
                        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
                        STATUS_BADGE_CLASSES[survey.status],
                      )}
                    >
                      {STATUS_LABELS[survey.status]}
                    </span>
                    <p className="font-semibold text-ink">{survey.name}</p>
                  </div>
                  <p className="text-xs text-muted">
                    {RESPONSE_FORMAT_LABELS[survey.responseFormat]} ・{' '}
                    {survey.questionCount}問
                  </p>
                  <p className="text-xs text-muted">
                    回答数 {survey.responseCount}件
                  </p>
                  <p className="text-xs text-muted">
                    作成日 {formatSurveyCreatedAt(survey.createdAt)}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      navigate(
                        orgPathFor(
                          routes.SURVEY_MANAGEMENT_EDIT.replace(
                            ':surveyId',
                            String(survey.id),
                          ),
                        ),
                      )
                    }
                  >
                    編集
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      navigate(
                        orgPathFor(
                          routes.SURVEY_MANAGEMENT_AGGREGATION.replace(
                            ':surveyId',
                            String(survey.id),
                          ),
                        ),
                      )
                    }
                  >
                    集計
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={busyId !== null}
                    onClick={() => void handleCopy(survey)}
                  >
                    コピー
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={busyId !== null}
                    onClick={() => {
                      setActionError(null)
                      setPendingAction({
                        type: 'status',
                        survey,
                        action: statusAction,
                      })
                    }}
                  >
                    {statusAction.label}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="danger"
                    disabled={busyId !== null}
                    onClick={() => {
                      setActionError(null)
                      setPendingAction({ type: 'delete', survey })
                    }}
                  >
                    削除
                  </Button>
                </div>
              </div>
            )
          })}
        </div>
      </section>

      <ConfirmModal
        open={pendingDelete !== null}
        textTitle="アンケートを削除しますか？"
        textMessage={
          pendingDelete
            ? `「${pendingDelete.survey.name}」を削除します。回答データや設置先があるアンケートは削除できません。`
            : undefined
        }
        textCancel="キャンセル"
        textConfirm="削除する"
        confirmButtonClassName="bg-red-600 hover:bg-red-700"
        isLoading={busyId !== null}
        onClose={closePendingAction}
        onConfirm={() => void handleConfirmPendingAction()}
      />

      <ConfirmModal
        open={pendingStatus !== null}
        textTitle={pendingStatus?.action.title ?? ''}
        textMessage={
          pendingStatus
            ? pendingStatus.action.message(pendingStatus.survey.name)
            : undefined
        }
        textCancel="キャンセル"
        textConfirm={pendingStatus?.action.confirm ?? ''}
        isLoading={busyId !== null}
        onClose={closePendingAction}
        onConfirm={() => void handleConfirmPendingAction()}
      />
    </>
  )
}
